import { useEffect, useMemo, useRef } from 'react';
import type { DocumentModel } from 'shared/domain/document/documentModel';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import { useDocumentsStore } from '../store';
import { isDocumentScopeLoading } from '../model';
import type { DocumentQuery } from '../types';

const NO_DOCUMENTS: DocumentModel[] = [];

interface UseDocumentsResult {
  documents: DocumentModel[];
  loading: boolean;
  scopeKey: string | undefined;
}

/** The documents of a scope (an issue, an inspection, a template), kept fresh while mounted. */
export function useDocuments(query: DocumentQuery | undefined): UseDocumentsResult {
  const result = useOpenScopeDocuments(query);
  const queryRef = useRef(query);
  queryRef.current = query;
  const openScope = useDocumentsStore.use.openScope();
  const closeScope = useDocumentsStore.use.closeScope();
  const { scopeKey } = result;

  useEffect(() => {
    const scopeQuery = queryRef.current;
    if (!scopeKey || !scopeQuery) return;

    openScope(scopeQuery);
    return (): void => closeScope(scopeQuery);
  }, [scopeKey, openScope, closeScope]);

  return result;
}

/** The documents of a scope that another component keeps open (with `useDocuments`). */
export function useOpenScopeDocuments(query: DocumentQuery | undefined): UseDocumentsResult {
  const scopeKey = useMemo(() => getDocumentScopeKey(query), [query]);
  const documents = useDocumentsStore((state) =>
    scopeKey ? state.documentsByScope[scopeKey] ?? NO_DOCUMENTS : NO_DOCUMENTS
  );
  const loading = useDocumentsStore((state) =>
    scopeKey
      ? isDocumentScopeLoading(state.loadingScopes[scopeKey], state.documentsByScope[scopeKey])
      : false
  );

  return { documents, loading, scopeKey };
}
